import AsyncStoreService from '../../services/async-store.service';
import {
  tripSearchApi,
  cartViewApi,
  getTravelCartApi,
  redeemCalendarApi,
} from '../../services/api/search.service';
import { moveNext } from './workflow';
import { openAlert } from './alert';
import { setRailResults } from './rail';
import { selectAccessToken, selectCompanyConfig } from '../selectors/auth.selector';
import { selectTripResults, selectSearchInput } from '../selectors/search.selector';
import to from '../../utils/to';
import { logException } from '../../utils/logger.utils';
import * as RootNavigation from '../../utils/navigationRef';
import i18n from '../../i18n';
import { fetchLowPriceFares } from '../../services/low-price-calendar.service';
import { selectCheckedRebookPassengers } from '../selectors/mytrips.selector';
import { REBOOK_ENABLED } from '../../config/env';

export const START_TRIP_SEARCH = 'START_TRIP_SEARCH';
export const SET_SEARCH_INPUT = 'SET_SEARCH_INPUT';
export const SET_LOW_FARES = 'SET_LOW_FARES';
export const SET_GEOLOCATION = 'SET_GEOLOCATION';
export const SET_LOCATION_DETAIL = 'SET_LOCATION_DETAIL';
export const SET_TRIP_SEARCH_RESULTS = 'SET_TRIP_SEARCH_RESULTS';
export const TRIP_SEARCH_FAILED = 'TRIP_SEARCH_FAILED';
export const SET_TRIP_SUMMARY = 'SET_TRIP_SUMMARY';
export const SET_TRAVEL_CART = 'SET_TRAVEL_CART';
export const UPDATE_PASSENGER_DETAILS = 'UPDATE_PASSENGER_DETAILS';
export const UPDATE_PASSENGER_DETAILS_REBOOK = 'UPDATE_PASSENGER_DETAILS_REBOOK';
export const UPDATE_REPORTING_FIELDS = 'UPDATE_REPORTING_FIELDS';
export const SET_SEATS_FOR_REBOOK_PASSENGERS = 'SET_SEATS_FOR_REBOOK_PASSENGERS';
export const ADD_TRIP_NAME = 'ADD_TRIP_NAME';
export const SET_CHANGERES = 'SET_CHANGERES';
export const UPDATE_AIR_FARE = 'UPDATE_AIR_FARE';
export const FRONTIER_FLIGHT_REIMBURSMENT_UPDATE = 'FRONTIER_FLIGHT_REIMBURSMENT_UPDATE';
export const UPDATE_SELECTED_AWARD = 'UPDATE_SELECTED_AWARD';
export const HOMEPAGE_ONEWAY_UDID_DATA = 'HOMEPAGE_ONEWAY_UDID_DATA';

export const setSearchInput = (searchInput, searchInputState) => {
  return async (dispatch) => {
    await AsyncStoreService.setSearchInput(searchInputState);
    dispatch({ type: SET_SEARCH_INPUT, searchInput });
  };
};

export const getLowFares = (fromCity, toCity, callNo = 0) => {
  return async (dispatch) => {
    const lowFares = await fetchLowPriceFares(fromCity, toCity, callNo);
    dispatch({ type: SET_LOW_FARES, lowFares });
  };
};

export const resetPriceOnCalendar = () => ({ type: SET_LOW_FARES });

export const tripSearchFailed = () => ({ type: TRIP_SEARCH_FAILED });

export const setGeolocation = (latitude, longitude, airportCode) => {
  return { type: SET_GEOLOCATION, latitude, longitude, airportCode };
};

export const setLocationDetail = (addressObj) => {
  return { type: SET_LOCATION_DETAIL, addressObj };
};

export const getRedeemCalendar = (authNumber) => {
  return async (dispatch, getState) => {
    const accessToken = selectAccessToken(getState());
    const results = selectTripResults(getState());
    const requestKey = results.TravelRequestId;
    return await redeemCalendarApi(accessToken, requestKey, authNumber);
  };
};

export const tripSearch = (searchOriginScreen, searchInputState, changeRes) => {
  return async (dispatch, getState) => {
    dispatch({ type: START_TRIP_SEARCH });
    const state = getState();
    const searchInput = selectSearchInput(state);
    const accessToken = selectAccessToken(state);
    const rebookPassengers = selectCheckedRebookPassengers(state);
    const getCompanyCulture = selectCompanyConfig(state);
    try {
      const results = await tripSearchApi(
        accessToken,
        searchInput,
        rebookPassengers,
        getCompanyCulture
      );
      if (changeRes) {
        results.TripId = changeRes.TripId;
      }
      dispatch({
        type: SET_TRIP_SEARCH_RESULTS,
        tripResults: results,
      });
      if (results.RailResponses) {
        dispatch(setRailResults(1, results.RailResponses));
      }
      if (changeRes) {
        dispatch(moveNext(searchOriginScreen, changeRes));
      } else {
        dispatch(moveNext(searchOriginScreen));
      }
    } catch (ex) {
      dispatch(tripSearchFailed());
      dispatch(openAlert({ message: ex.message }));
      logException(ex, {
        metaData: {
          message: 'Trip Search Failed.',
          ex,
        },
      });
    }
  };
};

export const getCartView = (changeResData, selectionData) => {
  return async (dispatch, getState) => {
    const state = getState();
    const tripResults = selectTripResults(state);
    const accessToken = selectAccessToken(state);
    const res = Object.assign({}, tripResults);
    if (changeResData) {
      const allLegs = changeResData?.AirResponseDetails?.AirLegs.map((airLeg) => {
        return ':' + airLeg.LegNumber;
      });
      tripResults.TripId = changeResData?.TripId;
      res['SelectedChangeLegs'] = allLegs.join('');
    }
    res['RepriceItinerary'] = true; // Added to get reprice values, necessary to enable PTA
    const [err, tripSummary] = await to(cartViewApi(accessToken, res, selectionData));
    if (err) {
      dispatch(
        openAlert({
          title: i18n.t('GetCartFailureMessage'),
          confirmText: i18n.t('GoBack'),
          onConfirmPressed: () => RootNavigation.goBack(),
        })
      );
      logException(err, { metaData: { travelRequestId: tripResults?.travelRequestId } });
    } else {
      dispatch({
        type: SET_TRIP_SUMMARY,
        tripSummary,
      });
    }
  };
};

export const getTravelCart = () => {
  return async (dispatch, getState) => {
    const state = getState();
    const accessToken = selectAccessToken(state);
    const searchInput = selectSearchInput(state);
    const travelRequestId = selectTripResults(state)?.TravelRequestId;
    const IsAirPolicyBreached = state?.search?.tripSummary?.IsAirPolicyBreached;
    const IsHotelPolicyBreached = state?.search?.tripSummary?.IsHotelPolicyBreached;
    const IsCarPolicyBreached = state?.search?.tripSummary?.IsCarPolicyBreached;
    const HotelInclusion = state?.search?.tripSummary?.HotelInclusion;
    const Passengers = state?.search?.tripSummary?.Passengers;
    const Supplier = state?.search?.tripSummary?.HotelResponses[0]?.Supplier;
    const policyBreachedObj = { IsAirPolicyBreached, IsHotelPolicyBreached, IsCarPolicyBreached };

    const travelCart = await getTravelCartApi(
      accessToken,
      searchInput,
      travelRequestId,
      policyBreachedObj,
      Passengers,
      Supplier
    );
    dispatch({
      type: SET_TRAVEL_CART,
      travelCart: { ...travelCart, HotelInclusion },
    });
  };
};

export const changeResPayload = (changeResData) => {
  return async (dispatch) => {
    dispatch({ type: SET_CHANGERES, changeResData });
  };
};

export function updatePassenger(passengers, deploymentMap) {
  return REBOOK_ENABLED
    ? { type: 'UPDATE_PASSENGER_DETAILS_REBOOK', passengers }
    : { type: 'UPDATE_PASSENGER_DETAILS', passengers, deploymentMap };
}

export function updateReportingFields(passengers, deploymentMap) {
  return { type: UPDATE_REPORTING_FIELDS, passengers, deploymentMap };
}

export function addTripName(tripName) {
  return { type: 'ADD_TRIP_NAME', tripName };
}

export function updatePassengersSeats(seats) {
  return { type: SET_SEATS_FOR_REBOOK_PASSENGERS, seats };
}

export function frontierFlightReimbursmentUpdate(address) {
  return { type: FRONTIER_FLIGHT_REIMBURSMENT_UPDATE, address };
}

export function updateSelectedAward(awardName) {
  return { type: UPDATE_SELECTED_AWARD, awardName };
}

export function addHomepageUDIDdata(data) {
  return { type: HOMEPAGE_ONEWAY_UDID_DATA, data };
}
